import { Paper, Typography, Box } from '@mui/material';
import { LiveCameraPlayer, PlaybackCamera } from 'components/CameraPlayer';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { accountSelectedSelector, playbackDateAtom } from 'atoms';
import CamerasList from './CamerasList';
import { useUpdateEffect } from 'react-use';
import ErrorBoundary from 'components/ErrorBoundary';
import { ErrorBoundaryContent } from 'components/ErrorBoundary/ErrorBoundary';
import CustomMosaicSelect from './CustomMosaicSelect';
import { useGetCamerasListApi } from 'hooks';
import { Account } from 'types/Account';

function CameraViewPage() {
  const accountSelected = useRecoilValue(accountSelectedSelector);

  if (!accountSelected) {
    return <Navigate to="/" />;
  }
  return <CameraViewPageContent {...{ accountSelected }} />;
}

function CameraViewPageContent({ accountSelected }: { accountSelected: Account }) {
  const { t } = useTranslation(['_common', 'cameras']);
  const [playbackDate, setPlaybackDate] = useRecoilState(playbackDateAtom);
  const { camName } = useParams();
  const navigate = useNavigate();
  const [camNamesToShow, setCamNamesToShow] = React.useState<string[] | null>(null);
  const [searchParams] = useSearchParams();
  const {
    cameras,
    loading: loadingCamerasList,
    error: errorFetchingCamerasList,
    refetch: refetchCamerasList,
  } = useGetCamerasListApi({ accountSelected });

  useUpdateEffect(() => {
    navigate(`/cameras?${searchParams.toString()}`);
  }, [accountSelected?.id]);

  useUpdateEffect(
    function resetPlayback() {
      setPlaybackDate(null);
    },
    [accountSelected?.id, camName],
  );

  React.useEffect(
    function whenChangeCamsToShow() {
      // if actual cam is not to show, must redirect to the first cam to show
      if (camNamesToShow) {
        navigate(`/cameras/${camNamesToShow[0]}?${searchParams.toString()}`);
      }
    },
    [camNamesToShow, navigate, searchParams],
  );

  if (!camName) {
    throw new Error('Parâmetro inválido (camName)');
  }

  if (errorFetchingCamerasList) {
    throw errorFetchingCamerasList;
  }

  return (
    <Box sx={{ mb: 2, height: '100%', display: 'flex' }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">{t('_common:cameras')}</Typography>
          <CustomMosaicSelect {...{ accountSelected, setCamNamesToShow, cameras }} />
        </Box>
        <ErrorBoundary
          key={camName}
          reloadChildrenOnTryAgain
          tryAgainClick={() => {}}
          fallback={(props) => (
            <Paper
              variant="outlined"
              sx={{
                variant: 'outlined',
                flex: '1 1 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxHeight: '662px',
                borderRadius: '5px',
                position: 'relative',
              }}
            >
              <ErrorBoundaryContent {...props} />
            </Paper>
          )}
        >
          {playbackDate ? (
            <PlaybackCamera {...{ accountSelected, camName, playbackDate }} />
          ) : (
            <LiveCameraPlayer {...{ camName, accountSelected }} />
          )}
        </ErrorBoundary>

        <CamerasList {...{ camNamesToShow, cameras, loadingCamerasList, refetchCamerasList }} />
      </Paper>
    </Box>
  );
}

export default CameraViewPage;
